import { Component, Input, OnInit, ViewChild, ViewEncapsulation, AfterViewInit, ElementRef, EventEmitter, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexYAxis,
  ApexTitleSubtitle,
  ApexLegend,
  ApexTooltip,
  ApexGrid,
} from 'ng-apexcharts';
import { DataHelper } from '../../helpers/data.helper';
import { UiHelper } from '../../helpers/ui.helper';
import { debounceTime } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { DataService } from '../../services/data.service';
import { UserHelper } from '../../helpers/user.helper';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ApiService } from '../../services/api.service';
import { LocalizationService } from '../../services/localization.service';
import { StandingsDialogComponent } from '../standings.dialog.component';
import { MatDialog } from '@angular/material/dialog';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  title: ApexTitleSubtitle;
  labels: string[];
  legend: ApexLegend;
  subtitle: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  colors: string[];
};

@Component({
  selector: 'cashout-new-slip',
  templateUrl: './cashout-new-slip.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./cashout.new.sass'],
})
export class CashoutNewSlip implements AfterViewInit, OnChanges, OnDestroy {
  @Input() possibleWin: any = '';
  @Input() isLive = true;
  @Input() stake = '2.00€';
  @Input() matchData: any = [];
  @Input() slipIsOpen = '';
  @Input() slipID = '';
  @Input() slipType = '';
  @Input() cashoutValue = 0.0;

  @Output() openSlip = new EventEmitter();

  @ViewChild('chart') chart: ChartComponent;
  @ViewChild('cashoutMain') cashoutMainRef: ElementRef;
  @ViewChild('matchName') matchName: ElementRef;
  @ViewChild('matchScoreMin') matchScoreMin: ElementRef;
  @ViewChild('matchDescriptionColumnOne') matchDescriptionColumnOne: ElementRef;
  @ViewChild('matchDescriptionColumnTwo') matchDescriptionColumnTwo: ElementRef;
  @ViewChild('matchOdd') matchOdd: ElementRef;
  public chartOptions: Partial<ChartOptions>;
  private subscriptionsToStopOnDestroy: Subscription[] = [];

  series = [
    [100, 0],
    [200, 0],
    [300, 0],
    [400, 0],
    [600, 0],
  ];

  resizeChangeListener = new Subject();

  CASHOUT_STATE: 'loading' | 'notGiving' | 'active' | 'confirm' = 'loading';

  constructor(
    public _DataHelper: DataHelper,
    public _UiHelper: UiHelper,
    private _DataService: DataService,
    public _UserHelper: UserHelper,
    private _router: Router,
    private _ApiService: ApiService,
    public _LocalizationService: LocalizationService,
    public dialog: MatDialog
  ) {
    this.subscriptionsToStopOnDestroy.push(
      this._UiHelper.windowResized.subscribe(x => {
        this.resizeChangeListener.next(x.w);
      })
    );

    this.subscriptionsToStopOnDestroy.push(
      this.resizeChangeListener.pipe(debounceTime(1000)).subscribe(width => {
        this.responsiveResolution();
      })
    );
    this.chartOptions = {
      series: [
        {
          name: 'STOCK ABC',
          data: this.series,
        },
      ],
      chart: {
        toolbar: {
          show: false,
        },
        type: 'area',
        height: 125,
        width: '100%',
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        colors: this._UiHelper.currentTheme === 'theme-dark' ? ['#6a8660'] : ['#d3edd2'],
        width: 1,
      },
      xaxis: {
        labels: {
          show: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        show: false,
        opposite: true,
        tooltip: {
          enabled: false,
        },
      },
      grid: {
        show: false,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
        row: {
          colors: undefined,
          opacity: 0.5,
        },
        column: {
          colors: undefined,
          opacity: 0.5,
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
      colors: this._UiHelper.currentTheme === 'theme-dark' ? ['#6a8660'] : ['#d3edd2'],
    };
  }

  ngAfterViewInit() {
    this.responsiveResolution();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.cashoutValue?.currentValue !== undefined) {
      if (changes.cashoutValue?.currentValue !== 0) {
        this.CASHOUT_STATE = 'active';
      } else {
        this.CASHOUT_STATE = 'notGiving';
      }
    }
  }

  ngOnDestroy() {
    this.subscriptionsToStopOnDestroy.forEach(subscription => subscription && subscription.unsubscribe());
  }

  responsiveResolution() {
    if (!(this.cashoutMainRef?.nativeElement && this.matchScoreMin?.nativeElement && this.matchDescriptionColumnOne?.nativeElement && this.matchOdd.nativeElement)) {
      return;
    }
  }

  trackBySlipId(index: number, item: any) {
    return item.matchId;
  }

  goEventDetail(matchId: string, isLive: boolean, isEnded?: boolean): Promise<any> {
    if (isEnded === true) {
      this.openStatsDialog(matchId);
    }
    return this._router.navigate([isLive ? '/liveeventdetail' : '/eventdetail', matchId]).then();
  }

  goSlipDetail(): void {
    return this._UiHelper.b3000SetAcountMobileState('open', 1, parseInt(this.slipID));
  }

  handleCashoutPay(state: 'cancel' | 'confirm') {
    if (state === 'cancel') {
      if (this.cashoutValue !== undefined) {
        if (this.cashoutValue !== 0) {
          this.CASHOUT_STATE = 'active';
        } else {
          this.CASHOUT_STATE = 'notGiving';
        }
      } else {
        this.CASHOUT_STATE = 'notGiving';
      }
    }
    if (state === 'confirm' && this.CASHOUT_STATE === 'active') {
      this.CASHOUT_STATE = 'confirm';
    }
  }

  handleCashoutConfirm() {
    this._UiHelper.setLoadingDialog(true);
    const translator = new TranslatePipe(this._LocalizationService);

    setTimeout(
      () => {
        this._ApiService.cashOutPay(this.slipID, this.cashoutValue).then(result => {
          const cv = this._DataHelper.cashoutSlipsList.filter(ocv => ocv.SlipId == this.slipID);

          this._UiHelper.setLoadingDialog(false);
          if (result.code == 'error') {
            this._UiHelper.errorSnackBar(result.message, '');
          } else if (result.code == 'errorcode') {
            const t_message = translator.transform(result.message, null);
            this._UiHelper.errorSnackBar(t_message, '');
          } else if (result.code == 'success') {
            const t_message = translator.transform('cashoutsuccess', null);
            this._UiHelper.successSnackBar(t_message, '');
            setTimeout(() => {
              this._DataService.fireApiCall('getSlipCashOutList');
            }, 1000);
            this._ApiService.getClient().then(u => {
              this._UserHelper.setUser(u);
            });
            // this._router.navigate(['/home']);
          }
        });
      },
      this.isLive ? 8 * 1000 : 0
    );
  }

  getCashoutValue() {
    try {
      let _cashoutValue = '0.00';

      if (typeof this.cashoutValue === 'string') {
        _cashoutValue = this.cashoutValue;
      }

      if (typeof this.cashoutValue === 'number') {
        _cashoutValue = this.cashoutValue.toFixed(2);
      }

      return _cashoutValue;
    } catch (e) {
      return '0.00';
    }
  }

  openStatsDialog(betradarMatchId: string) {
    this.dialog.open(StandingsDialogComponent, {
      id: 'standingsDialog',
      panelClass: 'standingsDialogClass',
      height: '90%',
      width: '90%',
      data: {
        tournamentId: '',
        sportId: '',
        betradarMatchId: betradarMatchId,
        type: 'endedmatchresult',
      },
    });
  }
}
